import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PageTemplate } from '@/components/templates'
import { CONTAINER_SIZES } from '@/constants'
import { UnlocksTable } from './components'
import { useUnlocks, useUnlocksStats } from '@/hooks/useUnlocks'
import { FundingMetricCard, MiniBarChart, MiniLineChart, MiniAreaChart, MiniWaveChart } from '@/components/molecules'
import { formatCompactNumber, formatMoney } from '@/utils'
import type { UnlocksQueryParams } from '@/types/unlocks'

const UnlocksScreen = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  
  // Filter states - initialize from URL params
  const [search, setSearch] = useState(() => searchParams.get('search') || '')
  const [sortBy, setSortBy] = useState<UnlocksQueryParams['sort_by']>(() => {
    const sort = searchParams.get('sort_by') as UnlocksQueryParams['sort_by']
    return sort || 'VESTING_DATE_START'
  })
  const [sortDirection, setSortDirection] = useState<UnlocksQueryParams['sort_direction']>(() => {
    const direction = searchParams.get('sort_direction') as UnlocksQueryParams['sort_direction']
    return direction || 'DESC'
  })

  // Update URL parameters helper
  const updateURLParams = useCallback((updates: Record<string, string | undefined>) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev)
      
      Object.entries(updates).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          newParams.set(key, String(value))
        } else {
          newParams.delete(key)
        }
      })
      
      return newParams
    })
  }, [setSearchParams])

  // Wrapped setters that also update URL
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value)
    updateURLParams({ search: value || undefined })
  }, [updateURLParams])

  const handleSortByChange = useCallback((value: UnlocksQueryParams['sort_by']) => {
    setSortBy(value)
    updateURLParams({ sort_by: value })
  }, [updateURLParams])

  const handleSortDirectionChange = useCallback((value: UnlocksQueryParams['sort_direction']) => {
    setSortDirection(value)
    updateURLParams({ sort_direction: value })
  }, [updateURLParams])

  // Sync state when URL params change externally (browser back/forward)
  useEffect(() => {
    const urlSearch = searchParams.get('search') || ''
    const urlSortBy = (searchParams.get('sort_by') as UnlocksQueryParams['sort_by']) || 'VESTING_DATE_START'
    const urlSortDirection = (searchParams.get('sort_direction') as UnlocksQueryParams['sort_direction']) || 'DESC'
    
    if (urlSearch !== search) setSearch(urlSearch)
    if (urlSortBy !== sortBy) setSortBy(urlSortBy)
    if (urlSortDirection !== sortDirection) setSortDirection(urlSortDirection)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])
  
  const { data: stats, loading: statsLoading } = useUnlocksStats()

  return (
    <PageTemplate maxWidth={CONTAINER_SIZES.WIDE}>
      <div className="space-y-6">
        {/* Page Header - aligned left as in Figma */}
        <div className="flex flex-col items-start">
          <h1 className="font-['Plus_Jakarta_Sans'] font-semibold leading-[40px] text-[#2d2923] dark:text-[#fdfdfd] text-[30px] tracking-[-0.28px] mb-2">
            Token Unlocks
          </h1>
          <p className="font-['Plus_Jakarta_Sans'] font-normal leading-[24px] text-[16px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)] w-[385px]">
            Track token unlock schedules and vesting data
          </p>
        </div>

        {/* Metrics Cards */}
        {statsLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-2">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-[100px] bg-white dark:bg-[#121212] animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-2">
            <FundingMetricCard
              icon="unlock"
              label="Upcoming Unlocks (6mo)"
              value={stats ? formatCompactNumber(stats.upcoming_unlocks_6mo) : '—'}
              percentChange={
                stats && stats.upcoming_unlocks_6mo_pct_change != null
                  ? {
                      value: `${Math.abs(stats.upcoming_unlocks_6mo_pct_change).toFixed(1)}%`,
                      direction: stats.upcoming_unlocks_6mo_pct_change >= 0 ? 'up' : 'down',
                      color: stats.upcoming_unlocks_6mo_pct_change >= 0 ? 'green' : 'red',
                    }
                  : undefined
              }
              chart={<MiniAreaChart />}
            />
            <FundingMetricCard
              icon="amount"
              label="Total Value"
              value={stats ? formatMoney(stats.total_value) : '—'}
              percentChange={
                stats && stats.total_value_pct_change != null
                  ? {
                      value: `${Math.abs(stats.total_value_pct_change).toFixed(1)}%`,
                      direction: stats.total_value_pct_change >= 0 ? 'up' : 'down',
                      color: stats.total_value_pct_change >= 0 ? 'green' : 'red',
                    }
                  : undefined
              }
              chart={<MiniWaveChart />}
            />
            <FundingMetricCard
              icon="chart"
              label="Next 30d Value"
              value={stats ? formatMoney(stats.next_30d_value) : '—'}
              percentChange={
                stats && stats.next_30d_value_pct_change != null
                  ? {
                      value: `${Math.abs(stats.next_30d_value_pct_change).toFixed(1)}%`,
                      direction: stats.next_30d_value_pct_change >= 0 ? 'up' : 'down',
                      color: stats.next_30d_value_pct_change >= 0 ? 'green' : 'red',
                    }
                  : undefined
              }
              chart={<MiniBarChart />}
            />
            <FundingMetricCard
              icon="calendar"
              label="Next 30d Events"
              value={stats ? formatCompactNumber(stats.next_30d_events) : '—'}
              percentChange={
                stats && stats.next_30d_events_pct_change != null
                  ? {
                      value: `${Math.abs(stats.next_30d_events_pct_change).toFixed(1)}%`,
                      direction: stats.next_30d_events_pct_change >= 0 ? 'up' : 'down',
                      color: stats.next_30d_events_pct_change >= 0 ? 'green' : 'red',
                    }
                  : undefined
              }
              chart={<MiniLineChart />}
            />
          </div>
        )}
        
        
        <UnlocksTable
          title="Token Unlocks"
          navigate={navigate}
          useDataHook={useUnlocks}
          dataKey="unlocks"
          showPagination={true}
          search={search}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSearchChange={handleSearchChange}
          onSortByChange={handleSortByChange}
          onSortDirectionChange={handleSortDirectionChange}
        />
      </div>
    </PageTemplate>
  )
}

export default UnlocksScreen 